import React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import SendIcon from '@mui/icons-material/Send';
import { InfoMessageService, TranslationService } from '../../../../core/services';
import { emptyNewMessage, NewMessage } from '../types/NewMessage';
import { useAppContext } from '../../../../AppContext';

type Props = {
  messageSizeLimit: number;
  onSendNewMessage(message: NewMessage): Promise<void>;
};

const NewMessageForm = (props: Props) => {
  const context = useAppContext();
  const [message, setMessage] = React.useState<NewMessage>({ ...emptyNewMessage });

  const getThemeBgColor = () => {
    const messageModule = context.styles['module:messages'];
    const brandColors = context.styles['general:colors']?.brand;
    if (messageModule?.backgroundColor) return messageModule.backgroundColor;
    if (brandColors) return brandColors.split(',')[0];
    return '#003f5f';
  };

  const getThemeTextColor = () => {
    const messageModule = context.styles['module:messages'];
    if (messageModule?.textColor) return messageModule.textColor;
    return '#ffffff';
  };

  const bgColor = getThemeBgColor();
  const textColor = getThemeTextColor();

  const sendMessage = async () => {
    if (message.content.length < 10) {
      const msg = TranslationService.translate('NewMessage_ErrorMessageMinMessageLength');
      const mergedMessage = msg.replace('{0}', '10');
      InfoMessageService.warning(mergedMessage);
    } else if (message.content.length > props.messageSizeLimit) {
      const msg = TranslationService.translate('MsgMaxLength');
      const mergedMessage = msg.replace('{0}', props.messageSizeLimit.toString());
      InfoMessageService.warning(mergedMessage);
    } else {
      await props.onSendNewMessage(message);
      setMessage({ ...emptyNewMessage });
    }
  };

  return (
    <Box sx={{ bgcolor: bgColor, color: textColor, p: 2, flexShrink: 0 }}>
      <TextField
        multiline
        fullWidth
        minRows={3}
        maxRows={5}
        value={message.content}
        onChange={(e) => setMessage({ ...message, content: e.target.value })}
        placeholder={TranslationService.translate('NewMessage_WriteMessagePlaceholder')}
        sx={{
          mb: 1.5,
          '& .MuiOutlinedInput-root': {
            bgcolor: '#fff',
            fontSize: 14,
          },
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          endIcon={<SendIcon />}
          onClick={sendMessage}
          sx={{
            bgcolor: '#fff',
            color: bgColor,
            fontWeight: 'bold',
            textTransform: 'uppercase',
            '&:hover': { bgcolor: '#e0e0e0' },
          }}
        >
          {TranslationService.translate('Send')}
        </Button>
      </Box>
    </Box>
  );
};

export default NewMessageForm;
